/**
 * Organization Settings Tab - Sub-tabbed layout
 * Hosts four sub-tabs: General, Security, Billing, SLA.
 */

import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Building2, Shield, CreditCard, Timer } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { GeneralSettingsTab } from './GeneralSettingsTab';
import { SecuritySettingsTab } from './SecuritySettingsTab';
import { BillingSettingsTab } from './BillingSettingsTab';
import { SlaSettingsTab } from './SlaSettingsTab';

// ---------------------------------------------------------------------------
// Sub-tab types
// ---------------------------------------------------------------------------

type OrgSubTab = 'general' | 'security' | 'billing' | 'sla';

// ---------------------------------------------------------------------------
// Styled components — mirrors SettingsPage top-level tab styling
// ---------------------------------------------------------------------------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;


const SubTabBar = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  gap: ${({ theme }) => theme.spacing[1]};
`;

const SubTab = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  border: none;
  background: transparent;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary.base : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  position: relative;
  transition: ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme, $isActive }) =>
      $isActive ? theme.colors.primary.base : 'transparent'};
    transition: ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary.base};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TabContent = styled.div``;

// ---------------------------------------------------------------------------
// Sub-tab definitions
// ---------------------------------------------------------------------------

const SUB_TABS: {
  id: OrgSubTab;
  label: string;
  icon: React.ReactNode;
  permission?: string;
}[] = [
  { id: 'general', label: 'General', icon: <Building2 /> },
  { id: 'security', label: 'Security', icon: <Shield />, permission: 'org:settings:manage' },
  { id: 'billing', label: 'Billing', icon: <CreditCard /> },
  { id: 'sla', label: 'Resolution Timelines', icon: <Timer />, permission: 'org:settings:manage' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const OrganizationSettingsTab = () => {
  const { hasPermission, isSuperAdmin } = useAuthStore();

  const visibleTabs = useMemo(
    () =>
      SUB_TABS.filter(
        (tab) => !tab.permission || isSuperAdmin() || hasPermission(tab.permission),
      ),
    [hasPermission, isSuperAdmin],
  );

  const [activeSubTab, setActiveSubTab] = useState<OrgSubTab>(
    visibleTabs[0]?.id ?? 'general',
  );

  return (
    <Container>
      <SubTabBar role="tablist" aria-label="Organization settings sections">
        {visibleTabs.map((tab) => (
          <SubTab
            key={tab.id}
            role="tab"
            aria-selected={activeSubTab === tab.id}
            $isActive={activeSubTab === tab.id}
            onClick={() => setActiveSubTab(tab.id)}
          >
            {tab.icon}
            {tab.label}
          </SubTab>
        ))}
      </SubTabBar>

      <TabContent role="tabpanel">
        {activeSubTab === 'general' && <GeneralSettingsTab />}
        {activeSubTab === 'security' && <SecuritySettingsTab />}
        {activeSubTab === 'billing' && <BillingSettingsTab />}
        {activeSubTab === 'sla' && <SlaSettingsTab />}
      </TabContent>
    </Container>
  );
};

export default OrganizationSettingsTab;
